import React, { useState, useEffect, useContext } from "react";
import LayoutCatalog from "../../../components/layout/LayoutCatalog";
import Item from "../../../components/common/Item";
import SliderFilter from "../../../components/common/SliderFilter";
import TagsFilter from "../../../components/common/TagsFilter";
import styled from "styled-components";
import { Query } from "react-apollo";
import gql from "graphql-tag";
import getUndef from "../../../lib/get-undef";
import { GET_PRODUCTS } from "../../../queries/Products";
import { products, productsVariables } from "../../../__generated__/products";
import Spinner from "../../../components/common/Spinner";
import HelmetComponent from "../../../components/layout/HelmetComponent";
import useCatalogScroll from "../../../components/hooks/use-catalog-scroll";
import useSorting from "../../../components/hooks/useSorting";
import { CityContext } from "../../../components/context/CityContext";

const GET_COLLECTIONS_FILTERS = gql`
  query collectionFilterProducts($slug: String!) {
    shop {
      id
      collections(where: { slug: $slug }) {
        id
        name
        metafields {
          key
          value
        }
        seo {
          title
          description
        }
        siblings {
          id
          name
          slug
        }
        tags
      }
    }
  }
`;

const GET_PRODUCTS_TAGS = gql`
  query tags(
    $first: Int
    $where: CollectionWhereInput
    $productsWhere: ProductWhereInput
  ) {
    shop {
      id
      collections(where: $where) {
        id

        products(first: $first, where: $productsWhere, orderBy: sort_ASC) {
          id
          tags
        }
      }
    }
  }
`;

const Page = ({ code, data, animate, tagsData }) => {
  const [show, setShow] = useState(!animate);
  const [filter, setFilter] = useState([]);
  const [slug, setSlug] = useState("");

  const { cityNameForSeo, phone } = useContext(CityContext);

  useEffect(() => {
    setShow(true);
  }, []);

  useCatalogScroll(code);

  useEffect(() => {
    setFilter([]);
    setSlug("");
  }, [code]);

  const collection = getUndef(() => data.shop.collections[0]);
  const isPromo = getUndef(() =>
    Boolean(
      collection.metafields.find(
        ({ key, value }) => key === "promoCollection" && value === "Да"
      )
    )
  );
  const tags = getUndef(() =>
    tagsData.shop.collections[0].products
      ?.filter((p) => p.tags.length > 0)
      ?.reduce((prev, p) => {
        p.tags.forEach((t) => {
          if (prev.indexOf(t) < 0) {
            prev.push(t);
          }
        });

        return prev;
      }, [])
  );
  const siblings = getUndef(() => collection.siblings);
  const collectionName = getUndef(() => collection.name) || "";

  const filterObjTags = {
    variants_some: {
      id_not: null,
    },
  };

  const filters =
    siblings && siblings.length
      ? [
          {
            slug: null,
            name: "Все",
            id: 0,
          },
          ...siblings.map((s) => ({
            slug: s.slug,
            name: s.name,
            id: s.id,
          })),
        ]
      : null;

  return (
    <LayoutCatalog nameArrow="КАТАЛОГ" href="/catalog">
      <LeftBlock show={show}>
        <h1 className="h2">{collectionName}</h1>
        {filters && (
          <SliderFilter
            options={filters}
            onChange={(value) => setSlug(value)}
          />
        )}
        {!isPromo && Array.isArray(tags) && tags.length > 0 ? (
          <TagsFilter
            tags={tags}
            onChange={(value) => {
              setFilter(value);
            }}
          />
        ) : null}
        <Table>
          <Query<products, productsVariables>
            query={GET_PRODUCTS}
            fetchPolicy="network-only"
            variables={{
              where: {
                slug: slug ? slug : code,
              },
              productsWhere: {
                active: true,
                ...filterObjTags,
              },
            }}
          >
            {({ loading, error, data }) => {
              if (error) {
                return null;
              }

              const collection = getUndef(() => data.shop.collections[0]);
              const slug = getUndef(() => collection.slug);
              const title = getUndef(() => collection.seo.title);
              const description = getUndef(() => collection.seo.description);
              const collectionProducts = (
                getUndef(() => collection.products) || []
              ).filter((p) => {
                if (!filter.length) {
                  return true;
                }
                for (const tag in filter) {
                  if (p.tags.findIndex((t) => t === filter[tag]) >= 0) {
                    return true;
                  }
                }
                return false;
              });

              const sortedProducts = useSorting(slug, collectionProducts);

              return (
                <>
                  <HelmetComponent
                    title={
                      collection?.seo?.title ||
                      `Доставка ${
                        title || (collection && collection.name)
                      } в ${cityNameForSeo} - МИКАFood`
                    }
                    description={
                      collection?.seo?.description ||
                      (description
                        ? `Доставляем вкусные ${description} в ${cityNameForSeo} на дом и в офис в течении часа. Заказ по телефону ${phone.default.number}`
                        : undefined)
                    }
                  />
                  {loading ? (
                    <Spinner />
                  ) : (
                    sortedProducts.map((i) => {
                      return (
                        <ItemTable key={i.id} {...i} slugCollection={code} />
                      );
                    })
                  )}
                </>
              );
            }}
          </Query>
        </Table>
      </LeftBlock>
    </LayoutCatalog>
  );
};

const Table = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  margin-right: -20px;

  @media (max-width: 800px) {
    margin-right: 0;
  }
`;

const ItemTable = styled(Item)`
  flex-basis: calc(33.3333% - 20px);
  max-width: calc(33.3333% - 20px);
  margin: 0 20px 20px 0;
  /* min-width: 330px; */
  @media (max-width: 1480px) {
    flex-basis: calc(50% - 20px);
    max-width: calc(50% - 20px);
  }

  @media (max-width: 800px) {
    flex-basis: 100%;
    max-width: 100%;
    margin-right: 0;
  }
`;

interface ILeftBlock {
  show: boolean;
}
const LeftBlock = styled.div<ILeftBlock>`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  max-width: calc(100% - 270px);
  transform: translate3d(0, 0, 0);
  opacity: ${({ show }) => (show ? 1 : 0)};
  transition: 0.2s 0.2s ease-out;

  @media (max-width: 1020px) {
    max-width: 100%;
  }
`;

Page.getInitialProps = async ({
  apolloClient,
  query: { code, animate },
}: any) => {
  const { data } = await apolloClient.query({
    query: GET_COLLECTIONS_FILTERS,
    variables: {
      slug: code,
    },
  });

  const { data: tagsData } = await apolloClient.query({
    query: GET_PRODUCTS_TAGS,
    variables: {
      where: {
        slug: code,
      },
      productsWhere: {
        active: true,
        variants_some: {
          id_not: null,
        },
      },
    },
  });

  return { code, data, animate: !!animate, tagsData };
};

export default Page;
